import {
  Home,
  Hammer,
  RefreshCw,
  Layers,
  Droplets,
  CloudLightning,
  Wind,
  ShieldCheck,
  AlarmClock,
  ArrowUpRight,
  BadgeCheck,
} from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { scrollToSection, requestLeadType } from "@/lib/scroll";

const ROOF_IMG =
  "https://images.pexels.com/photos/32163952/pexels-photo-32163952.jpeg?auto=compress&cs=tinysrgb&w=1400";
const SIDING_IMG =
  "https://images.pexels.com/photos/37181596/pexels-photo-37181596.jpeg?auto=compress&cs=tinysrgb&w=1200";

const SERVICES = [
  {
    icon: Home,
    title: "Residential Roofing",
    desc: "Complete roofing systems for your home — installed with care, premium materials, and workmanship we'd sign our name to.",
    img: ROOF_IMG,
    featured: true,
  },
  {
    icon: Hammer,
    title: "Roof Repair",
    desc: "Honest assessments and lasting repairs for leaks, wear, and storm damage.",
  },
  {
    icon: RefreshCw,
    title: "Roof Replacement",
    desc: "Full tear-off and replacement, done right the first time.",
  },
  {
    icon: Layers,
    title: "Siding Installation & Replacement",
    desc: "Beautiful, durable siding that protects and elevates your home.",
    img: SIDING_IMG,
  },
  {
    icon: Droplets,
    title: "Gutter Installation & Replacement",
    desc: "Seamless gutters that move water away and protect your foundation.",
  },
  {
    icon: CloudLightning,
    title: "Storm Damage Restoration",
    desc: "Full exterior restoration after hail, wind, and severe weather.",
  },
  {
    icon: Wind,
    title: "Hail & Wind Damage Inspections",
    desc: "Thorough, documented inspections — always free, always honest.",
  },
  {
    icon: ShieldCheck,
    title: "Insurance Restoration",
    desc: "Clear guidance through the restoration process after a storm event.",
  },
  {
    icon: AlarmClock,
    title: "Emergency Roof Repairs",
    desc: "Rapid response when your home can't wait. Call (317) 516-9317.",
  },
];

const ServiceCard = ({ service, index }) => {
  const Icon = service.icon;
  const num = String(index + 1).padStart(2, "0");
  return (
    <Reveal
      delay={(index % 3) * 0.08}
      className={`group relative overflow-hidden bg-ink ${
        service.featured ? "md:col-span-2 md:row-span-2" : ""
      }`}
    >
      <button
        data-testid={`service-card-${index + 1}`}
        onClick={() => {
          requestLeadType("inspection");
          scrollToSection("#contact");
        }}
        className="relative flex h-full min-h-[240px] w-full flex-col justify-between p-8 text-left md:p-10"
      >
        {service.img && (
          <>
            <img
              src={service.img}
              alt={service.title}
              className={`absolute inset-0 h-full w-full object-cover opacity-40 transition-[opacity,transform] duration-700 group-hover:scale-[1.04] group-hover:opacity-55 ${
                service.featured ? "" : "opacity-25"
              }`}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-ink/20" />
          </>
        )}
        <div className="relative flex items-start justify-between">
          <span className="font-serif text-sm italic text-gold/80">{num}</span>
          <ArrowUpRight className="h-5 w-5 text-gold opacity-0 transition-[opacity,transform] duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:opacity-100" />
        </div>
        <div className="relative mt-14">
          <Icon className="mb-5 h-6 w-6 text-gold" />
          <h3
            className={`font-serif font-medium tracking-tight text-white ${
              service.featured ? "text-3xl md:text-5xl" : "text-2xl"
            }`}
          >
            {service.title}
          </h3>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-white/55">{service.desc}</p>
        </div>
      </button>
      <span className="pointer-events-none absolute inset-x-0 bottom-0 h-px origin-left scale-x-0 bg-gold transition-transform duration-500 group-hover:scale-x-100" />
    </Reveal>
  );
};

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="relative py-28 md:py-40">
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="mb-16 flex flex-col justify-between gap-10 md:mb-20 lg:flex-row lg:items-end">
          <Reveal>
            <Eyebrow>What We Do</Eyebrow>
            <h2 className="mt-6 font-serif text-5xl font-medium leading-[1.02] tracking-tight md:text-6xl">
              Serving Homes.
              <br />
              <span className="italic text-gold">Above the Rest.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="flex flex-col items-start gap-3 lg:items-end">
            <span
              data-testid="services-free-inspection-chip"
              className="inline-flex items-center gap-2 border border-gold/40 bg-gold/10 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.25em] text-gold"
            >
              <BadgeCheck className="h-4 w-4" /> Free Roof Inspections
            </span>
            <span
              data-testid="services-free-quote-chip"
              className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.25em] text-white/60"
            >
              <BadgeCheck className="h-4 w-4 text-gold" /> Free Quotes — No Pressure
            </span>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 gap-px border border-white/10 bg-white/10 md:grid-cols-3">
          {SERVICES.map((service, i) => (
            <ServiceCard key={service.title} service={service} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
